import React, { createContext, ReactElement, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import _ from 'lodash';
import {
  ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES,
  ACTIVE_ACCOUNT_COOKIE_NAME,
  PAGINATION_10K_DEFAULTS,
} from '@common/constants';
import { Permission } from '@common/enums';
import { useAuthContextProvider } from '@common/hooks/auth/AuthContextProvider';
import FullPageSpinnerLayout from '@common/layouts/FullPageSpinnerLayout';
import {
  AccountSchema,
  useGetAccountQuery,
  useGetAccountsQuery,
  useGetAdvertisersQuery,
  useGetAgenciesQuery,
} from '@graphql/__generated__';
import { AccountContextProviderProps, AccountContextState, AccountItem, ISelectionItem } from './interfaces';

const AccountContext = createContext<AccountContextState>({
  accounts: [],
  agencies: [],
  advertisers: [],
  selectedAccounts: [],
  selectedAdvertisers: [],
  selectedAgencies: [],
  accountsLoading: false,
  advertisersLoading: false,
  agenciesLoading: false,
  setAccounts: undefined,
  setAdvertisers: undefined,
  setAgencies: undefined,
});

const itemMapper = (item: { name: string; id: string | number }): ISelectionItem => ({
  label: item.name,
  key: item.id,
});

const accountMapper = (item: AccountSchema): AccountItem => ({
  label: _.capitalize(item.name),
  key: item.id,
  rateCard: item.rateCard,
  currency: item.currency,
});

export const AccountContextProvider: React.FC<AccountContextProviderProps> = ({ children }): ReactElement => {
  const initialSelectedAccounts = Cookies.get(ACTIVE_ACCOUNT_COOKIE_NAME)
    ? JSON.parse(Cookies.get(ACTIVE_ACCOUNT_COOKIE_NAME))
    : [];

  const authContext = useAuthContextProvider();
  const [accountId] = [authContext?.userScope?.account_id].map(Number);

  const [selectedAccounts, setSelectedAccounts] = useState<AccountItem[]>(initialSelectedAccounts);

  const [selectedAgencies, setSelectedAgencies] = useState<ISelectionItem[]>([]);
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<ISelectionItem[]>([]);

  const permission_ids = authContext?.userScope?.permission_ids;
  const canAccessAllAccounts = permission_ids?.includes(Permission.AccessAllAccounts);

  useEffect(() => {
    if (authContext.isLoading) return;

    if (canAccessAllAccounts && initialSelectedAccounts.length > 0) {
      setSelectedAccounts(initialSelectedAccounts);
    }
  }, [authContext.isLoading]);

  const { data, loading: accountsLoading } = canAccessAllAccounts
    ? useGetAccountsQuery()
    : useGetAccountQuery({ variables: { id: Number(accountId) } });

  const { data: agenciesData, loading: agenciesLoading } = useGetAgenciesQuery({
    variables: {
      ...PAGINATION_10K_DEFAULTS,
      accountId: canAccessAllAccounts ? Number(selectedAccounts[0]?.key) : accountId,
      // userIds: canAccessAllAccounts ? null : [userID], ignore for now, we dont have easy way to set user_advertiser_visibility table
    },
  });

  const { data: advertisersData, loading: advertisersLoading } = useGetAdvertisersQuery({
    variables: {
      ...PAGINATION_10K_DEFAULTS,
      accountId: canAccessAllAccounts ? Number(selectedAccounts[0]?.key) : accountId,
      // userIds: canAccessAllAccounts ? null : [userID], ignore for now, we dont have easy way to set user_advertiser_visibility table
    },
  });

  const accounts = data ? ('account' in data ? [{ ...data.account }] : data.accounts?.nodes) : [];
  const mappedAccounts: AccountItem[] = accounts.map(accountMapper);

  useEffect(() => {
    if (
      !accountsLoading &&
      data &&
      !agenciesLoading &&
      agenciesData?.agencies &&
      !advertisersLoading &&
      advertisersData?.advertisers
    ) {
      const accountsIds = new Set(selectedAccounts.map(({ key }) => key));
      const agenciesIds = new Set(agenciesData.agencies.nodes.map((agency) => agency.id));

      const filteredAgencies = agenciesData.agencies.nodes
        .filter((agency) => accountsIds.has(agency.accountId))
        .map(itemMapper);
      const filteredAdvertisers = advertisersData.advertisers.nodes
        .filter((adv) => agenciesIds.has(adv.agencyId))
        .map(itemMapper);

      // setSelectedAccounts([filteredAccounts[0]]);
      setSelectedAgencies(filteredAgencies);
      setSelectedAdvertisers(filteredAdvertisers);
    }
  }, [accountsLoading, agenciesLoading, advertisersLoading, data, agenciesData, advertisersData]);

  useEffect(() => {
    if (data && !accountsLoading) {
      if (canAccessAllAccounts && initialSelectedAccounts.length > 0) {
        const matchingMappedAccount = mappedAccounts.find((account) => account.key === initialSelectedAccounts[0].key);
        setSelectedAccounts([matchingMappedAccount]);
        return;
      }
      Cookies.remove(ACTIVE_ACCOUNT_COOKIE_NAME, ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES);
      setSelectedAccounts([mappedAccounts[0]]);
    }
  }, [data, accountsLoading]);

  useEffect(() => {
    Cookies.set(ACTIVE_ACCOUNT_COOKIE_NAME, JSON.stringify(selectedAccounts), ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES);
  }, [selectedAccounts]);

  const accountsIds = new Set(selectedAccounts?.map((account) => account.key));
  const agenciesIds = new Set(selectedAgencies?.map((agency) => agency.key));

  const filteredAgencies =
    agenciesData?.agencies?.nodes?.filter((agency) => accountsIds.has(agency.accountId)).map(itemMapper) || [];
  const filteredAdvertisers =
    advertisersData?.advertisers?.nodes?.filter((adv) => agenciesIds.has(adv.agencyId)).map(itemMapper) || [];

  useEffect(() => {
    const accountsIds = new Set(selectedAccounts?.map((account) => account.key));
    const filteredAgencies =
      agenciesData?.agencies?.nodes?.filter((agency) => accountsIds.has(agency.accountId)).map(itemMapper) || [];

    setSelectedAgencies(filteredAgencies);
  }, [selectedAccounts]);

  useEffect(() => {
    const agenciesIds = new Set(selectedAgencies?.map((agency) => agency.key));
    const filteredAdvertisers =
      advertisersData?.advertisers?.nodes?.filter((adv) => agenciesIds.has(adv.agencyId)).map(itemMapper) || [];

    setSelectedAdvertisers(filteredAdvertisers);
  }, [selectedAgencies]);

  if (
    authContext.isLoading ||
    (authContext.isAuthenticated && !authContext.isA1Session && selectedAccounts.length === 0) ||
    accountsLoading ||
    agenciesLoading ||
    advertisersLoading
  ) {
    return <FullPageSpinnerLayout />;
  }

  return (
    <AccountContext.Provider
      value={{
        accounts: mappedAccounts,
        agencies: filteredAgencies,
        advertisers: filteredAdvertisers,
        selectedAccounts: selectedAccounts,
        selectedAdvertisers: selectedAdvertisers,
        selectedAgencies: selectedAgencies,
        accountsLoading: false,
        advertisersLoading: false,
        agenciesLoading: false,
        setAgencies: setSelectedAgencies,
        setAdvertisers: setSelectedAdvertisers,
        setAccounts: setSelectedAccounts,
      }}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccountContextProvider = (): AccountContextState => {
  return useContext(AccountContext);
};
